import { FC, useEffect, useState } from "react";
import { Font, Input, Modal } from "../../TrueUI";
import { useApiPost } from "../../../hooks";
import { PolicyBlob } from "../../../dtos/policy-blob";
import { CopyQuoteRequestDto } from "../../../dtos/copy-quote-request-dto";
import { isAPITotallyComplete } from "../../../utilities/apiFunctions";
import {
  INSURED_ATOM_KEY,
  INSURED_BODY_SECTION,
  INSURED_CHILD_ITEM,
  INSURED_PARENT_ITEM,
  POLICY_ID,
  QUOTE_ID,
} from "../../../utilities/queryStringsHash";
import { useAtomFamily } from "../../../hooks/useAtomFamily";
import { GlobalInsuredAtomFamily } from "../InsuredAtoms";
import { updateInsuredAtom } from "./updatesPolicyQuoteFunctions";
import { getPolicyQuoteInitialValues } from "./PolicyQuoteForm/PolicyQuoteUtils";
import { getQueryStringsURL } from "../../../utilities/URLUtilities_OBSOLETE";
import { SubSideNavItemDestinationsEnum } from "../../../dtos/sub-side-nav-item-destinations-enum";
import { SubSideNavParentsIndexEnum } from "../../../dtos/sub-side-nav-parents-index-enum";
import { SubSideNavChildrenIndexEnum } from "../../../dtos/sub-side-nav-children-index-enum";
import { useNavigate } from "react-router";

export type ModalCopyQuoteProps = {
  tabKey: string;
  policyId: number;
  quoteId: number;
  quoteNameFromQuoteToCopy: string;
  open: boolean;
  cancelEvent: () => void;
};

const ModalCopyQuote: FC<ModalCopyQuoteProps> = ({
  tabKey,
  policyId,
  quoteId,
  quoteNameFromQuoteToCopy,
  open,
  cancelEvent,
}) => {
  const navigate = useNavigate();
  const insuredIdAtom = `${INSURED_ATOM_KEY} ${tabKey}`;
  const { getAtom, setAtom } = useAtomFamily(
    GlobalInsuredAtomFamily(insuredIdAtom)
  );
  const [copyQuoteRequest, setCopyQuoteRequest] = useState<CopyQuoteRequestDto>(
    { policyId, quoteId, quoteName: `Copy of ${quoteNameFromQuoteToCopy}` }
  );

  const { responsePost, dispatchPost, validatorErrorResponse } =
    useApiPost<PolicyBlob>("api/QuotePolicy/CopyQuote", copyQuoteRequest);

  const openQuote = (policyId?: number | null, quoteId?: number | null) => {
    const atomValue = getAtom();

    const newAtomValue = updateInsuredAtom(
      atomValue ?? {},
      getPolicyQuoteInitialValues(policyId ?? 0, quoteId ?? 0, null)
    );
    setAtom(newAtomValue);

    const quoteHashes = getQueryStringsURL([
      {
        nameOfHash: INSURED_BODY_SECTION,
        valueOfHash: SubSideNavItemDestinationsEnum.POLICY_QUOTE_SECTIONS,
      },
      {
        nameOfHash: INSURED_PARENT_ITEM,
        valueOfHash: SubSideNavParentsIndexEnum.POLICY,
      },
      {
        nameOfHash: INSURED_CHILD_ITEM,
        valueOfHash: SubSideNavChildrenIndexEnum.POLICY_HISTORY,
      },
      {
        nameOfHash: POLICY_ID,
        valueOfHash: policyId?.toString() ?? "0",
      },
      { nameOfHash: QUOTE_ID, valueOfHash: quoteId?.toString() ?? "0" },
    ]);

    navigate(quoteHashes);
  };

  useEffect(() => {
    if (isAPITotallyComplete(responsePost) && validatorErrorResponse === null) {
      const policyJSONCopy = responsePost.axiosResponse?.data;
      cancelEvent();
      openQuote(policyJSONCopy?.policyID, policyJSONCopy?.quote?.quoteID);
    }
  }, [responsePost]);

  return (
    <Modal
      size="sm"
      id={"id_copy_quote_modal"}
      title={"Copy Quote"}
      open={open}
      cancelButtonWithConfirmation
      cancelEvent={cancelEvent}
      saveOverrideLabel="COPY"
      showCancelTextButton
      showCloseButton
      cancelButtonConfirmationText={
        "Are you sure you want to discard the changes?"
      }
      saveEvent={() => dispatchPost()}
    >
      <Font whiteSpace="normal">
        Clicking on the Copy button will create a copy of this quote. Enter a
        name for the new quote below to help you differentiate it from other
        versions.
      </Font>
      <div style={{ marginTop: "20px" }}>
        <Input
          id="id_quote_name"
          name="quote_name"
          label="Quote Name (Required)"
          value={copyQuoteRequest.quoteName}
          onChangeRawValue={(value) =>
            setCopyQuoteRequest({ ...copyQuoteRequest, quoteName: value ?? "" })
          }
          errorMessage={
            (validatorErrorResponse?.errorDetails?.quoteName as string[]) ??
            null
          }
        />
      </div>
    </Modal>
  );
};

export { ModalCopyQuote };
